/**
 * Sortable table header component
 */
import type { SortDirection } from '../../hooks/useSortableTable'

interface SortableHeaderProps {
  label: string
  direction: SortDirection | null
  onSort: () => void
  align?: 'left' | 'right'
}

/**
 * A clickable table header cell with sort indicators
 *
 * @example
 * <SortableHeader
 *   label="Symbol"
 *   direction={getSortDirection('symbol')}
 *   onSort={() => requestSort('symbol')}
 * />
 */
export function SortableHeader({ label, direction, onSort, align = 'left' }: SortableHeaderProps) {
  return (
    <th
      className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      onClick={onSort}
    >
      <span className="inline-flex items-center gap-1">
        {align === 'right' && <SortIndicator direction={direction} />}
        {label}
        {align === 'left' && <SortIndicator direction={direction} />}
      </span>
    </th>
  )
}

function SortIndicator({ direction }: { direction: SortDirection | null }) {
  if (!direction) {
    // Show subtle indicator that column is sortable
    return (
      <svg
        className="w-4 h-4 text-gray-300"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
        />
      </svg>
    )
  }

  if (direction === 'asc') {
    return (
      <svg
        className="w-4 h-4 text-primary-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    )
  }

  return (
    <svg
      className="w-4 h-4 text-primary-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  )
}
